import React, { useEffect, useState } from 'react';
import { Logo } from 'components/common/Logo/Logo';
import { LanguageSelector } from './components/LanguageSelector';
import { ProfileDropdown } from './components/MenuItem';
import useAuth from 'features/auth/hooks/useAuth';
import { MenuItem } from './types/types';
import { useTranslation } from 'react-i18next';
import { RoutesPaths } from 'routes/RoutesPathsEnum';
import { NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useMobileDetect } from 'utils/hooks';
import MobileMenu from './components/MobileMenu';
import { getCurrentUser } from 'features/auth/services/auth.service';
import { useQuery } from 'react-query';
import { Badge } from 'components/common/Badge/Badge';

const Header: React.FC = () => {
	const auth = useAuth();
	const { t } = useTranslation();
	const deviceInfo = useMobileDetect();
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const { getUser, setUser } = useAuth();
	const user = getUser();

	const { data, isSuccess } = useQuery(['getCurrentUser', user], {
		queryFn: () => getCurrentUser(),
		enabled: !user && !!auth.getAccessToken()
	});

	useEffect(() => {
		if (isSuccess) {
			setUser(data);
		}
	}, [data]);

	const menuItems: MenuItem[] = [
		{
			label: t('report.retake_test'),
			href: RoutesPaths.INSTRUCTIONS
		},
		{
			label: t('courses.courses'),
			href: RoutesPaths.COURSES,
			badge: 'new'
		},
		{
			label: t('profile.my_reports'),
			href: RoutesPaths.REPORTS
		},
		{
			label: t('profile.test_breakdown'),
			href: RoutesPaths.CHAT,
			badge: 'AI talent'
		}
	];

	return (
		<header className='z-10 w-full border-b border-gray-200 bg-white'>
			<div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
				<div className='flex h-16 items-center justify-between'>
					<div className='flex items-center'>
						<Logo />
					</div>
					<div className='flex items-center gap-6'>
						<nav className='hidden space-x-8 md:flex'>
							{auth.getAccessToken() &&
								menuItems.map(item => (
									<NavLink
										key={item.label}
										to={item.href ?? ''}
										className={({ isActive }) =>
											`relative px-1 py-5 text-sm font-medium transition-colors ${isActive ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'} `
										}
									>
										{item.label}

										<div className='absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 opacity-0 [.text-blue-600>&]:opacity-100' />
										{item.badge && (
											<div className='absolute -right-3 -top-0 flex items-center justify-center'>
												<div className='relative'>
													<Badge
														text={
															item.badge as string
														}
														variant='blue'
														className='animate-pulse py-0 text-xs'
													/>
												</div>
											</div>
										)}
									</NavLink>
								))}
						</nav>

						<div className='flex items-center gap-6'>
							<LanguageSelector />
							{auth.getAccessToken() &&
								!deviceInfo.isSmallScreen && (
									<ProfileDropdown />
								)}

							{auth.getAccessToken() &&
								deviceInfo.isSmallScreen && (
									<button
										className='p-2 text-gray-600 hover:text-gray-900 md:hidden'
										onClick={() =>
											setIsMenuOpen(!isMenuOpen)
										}
									>
										{isMenuOpen ? (
											<X size={24} />
										) : (
											<Menu size={24} />
										)}
									</button>
								)}
						</div>
					</div>
				</div>
			</div>

			{/* Mobile menu */}
			{auth.getAccessToken() && (
				<>
					<MobileMenu
						isMenuOpen={isMenuOpen}
						setIsMenuOpen={setIsMenuOpen}
					/>
					{/* Overlay */}
					{isMenuOpen && (
						<div
							className='fixed inset-0 z-40 bg-black bg-opacity-50 md:hidden'
							onClick={() => setIsMenuOpen(false)}
						/>
					)}
				</>
			)}
		</header>
	);
};

export default Header;
